import { useMemo } from 'react';
import Hero from '@/pages/mt4-mt5/components/Hero';
import PlatformChooser from '@/pages/mt4-mt5/components/PlatformChooser';
import SectionNav from '@/pages/mt4-mt5/components/SectionNav';
import GuideSection from '@/pages/mt4-mt5/components/GuideSection';
import ComparisonSection from '@/pages/mt4-mt5/components/ComparisonSection';
import DemoSection from '@/pages/mt4-mt5/components/DemoSection';
import LearningRoadmap from '@/pages/mt4-mt5/components/LearningRoadmap';
import BeginnerEssentials from '@/pages/mt4-mt5/components/BeginnerEssentials';
import PlatformFaq from '@/pages/mt4-mt5/components/PlatformFaq';
import NextLearning from '@/pages/mt4-mt5/components/NextLearning';
import { siteOrigin, useSeo } from '@/hooks/useSeo';
import { mt4TutorialGroups, mt5TutorialGroups } from '@/utils/mt4mt5Tutorials';
import { platformScreens, platformFaqs } from '@/mocks/mt4mt5Page';

const mt4Highlights = [
  'ดาวน์โหลด ติดตั้ง และล็อกอินครั้งแรก',
  'อ่านหน้าจอ Market Watch และ Navigator',
  'วาดเส้นและจัดการวัตถุบนกราฟ',
  'เปิด ปิด และแก้ไขคำสั่งซื้อขาย',
  'แก้ปัญหากราฟว่างและล็อกอินไม่ผ่าน',
];

const mt5Highlights = [
  'ติดตั้งและเข้าสู่ระบบบัญชี MT5',
  'เปลี่ยนกรอบเวลา 21 แบบ และจัดการกราฟ',
  'ค้นหาอินดิเคเตอร์และเครื่องมือวิเคราะห์',
  'เปิดปิดสถานะและตั้ง Stop Loss / Take Profit',
  'แก้ปัญหา MT5 ไม่แสดงกราฟ',
];

export default function Mt4Mt5() {
  const mt4Count = mt4TutorialGroups.reduce((sum, group) => sum + group.articles.length, 0);
  const mt5Count = mt5TutorialGroups.reduce((sum, group) => sum + group.articles.length, 0);

  const jsonLd = useMemo(() => {
    const origin = siteOrigin();
    const lessonList = [...mt4TutorialGroups, ...mt5TutorialGroups].flatMap((group) =>
      group.articles.map((article) => ({
        '@type': 'Article',
        headline: article.title,
        url: `${origin}/${article.slug}`,
      })),
    );

    return [
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: platformFaqs.map((faq) => ({
          '@type': 'Question',
          name: faq.q,
          acceptedAnswer: {
            '@type': 'Answer',
            text: [faq.a, faq.a2].filter(Boolean).join(' '),
          },
        })),
      },
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: 'คู่มือ MT4 และ MT5 สำหรับมือใหม่',
        description:
          'รวมบทความการใช้งาน MetaTrader 4 และ MetaTrader 5 ตั้งแต่ติดตั้ง ใช้งานกราฟ อินดิเคเตอร์ ไปจนถึงการจัดการคำสั่งซื้อขาย',
        inLanguage: 'th-TH',
        hasPart: lessonList,
      },
    ];
  }, []);

  useSeo({
    /* @replace:mt4-title */
    title: 'คู่มือ MT4 และ MT5 สำหรับมือใหม่: วิธีใช้งานและตั้งค่า',
    description:
      /* @replace:mt4-description */
      'เรียนรู้วิธีใช้งาน MT4 และ MT5 สำหรับมือใหม่ ตั้งแต่การติดตั้ง การตั้งค่ากราฟ การเพิ่มอินดิเคเตอร์ การจัดการคำสั่งซื้อขาย ไปจนถึงคำตอบเรื่องเวลาแพลตฟอร์ม ขนาด Lot และการใช้งานบนมือถือ',
    /* @replace:mt4-keywords */
    keywords: 'MT4, MT5, คู่มือ MT4, คู่มือ MT5, วิธีใช้ MetaTrader, ติดตั้ง MT4, ตั้งค่ากราฟ, อินดิเคเตอร์',
    canonicalPath: '/mt4-mt5',
    jsonLd,
  });

  return (
    <div className="bg-background-50">
      <Hero mt4Lessons={mt4Count} mt5Lessons={mt5Count} />
      <SectionNav />
      <PlatformChooser lessons={{ mt4: mt4Count, mt5: mt5Count }} />

      <GuideSection
        sectionId="mt4-guide"
        platform="MT4"
        fullName="MetaTrader 4"
        title="คู่มือการใช้งาน MT4"
        subtitle="เรียนรู้วิธีใช้ MT4 ทีละขั้นตอน ตั้งแต่การเริ่มต้นใช้งานไปจนถึงการจัดการกราฟและคำสั่งซื้อขาย"
        groups={mt4TutorialGroups}
        image={platformScreens.mt4.image}
        caption={platformScreens.mt4.caption}
        highlights={mt4Highlights}
        tone="primary"
      />

      <GuideSection
        sectionId="mt5-guide"
        platform="MT5"
        fullName="MetaTrader 5"
        title="คู่มือการใช้งาน MT5"
        subtitle="เรียนรู้การใช้งาน MT5 ตั้งแต่การติดตั้ง การจัดการกราฟ การใช้เครื่องมือวิเคราะห์ ไปจนถึงการจัดการคำสั่งซื้อขาย"
        groups={mt5TutorialGroups}
        image={platformScreens.mt5.image}
        caption={platformScreens.mt5.caption}
        highlights={mt5Highlights}
        tone="accent"
      />

      <ComparisonSection />
      <DemoSection />
      <LearningRoadmap />
      <BeginnerEssentials />
      <PlatformFaq />
      <NextLearning />
    </div>
  );
}